import { Link } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocalePath } from "../hooks/useLocalePath";
import { SEOHead } from "../components/SEOHead";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  ChevronRight, ChevronDown, Camera, ShieldCheck, HelpCircle,
  Compass, CheckCircle2, AlertTriangle, XCircle, Maximize,
  FileImage, Sun, User, Ruler, ClipboardList, Image,
} from "lucide-react";
import { SiteFooter } from "../components/SiteFooter";
import { DVGuidesNav } from "../components/DVGuidesNav";

const ruleIcons = [Maximize, Sun, Camera, XCircle];
const ruleColors = [
  "bg-teal-50 text-teal-700",
  "bg-sky-50 text-sky-700",
  "bg-violet-50 text-violet-700",
  "bg-amber-50 text-amber-700",
];

const specIcons = [Image, FileImage, Ruler];
const specColors = [
  "bg-teal-700 text-white",
  "bg-sky-600 text-white",
  "bg-violet-600 text-white",
];

const mistakeColors = [
  "bg-red-50 border-red-200",
  "bg-amber-50 border-amber-200",
  "bg-orange-50 border-orange-200",
  "bg-rose-50 border-rose-200",
];

export default function DVPhotoRequirementsPage() {
  const { t } = useTranslation();
  const lp = useLocalePath();
  const [openFaq, setOpenFaq] = useState(null);

  const rules = t("dvp.rules", { returnObjects: true });
  const specs = t("dvp.specs", { returnObjects: true });
  const mistakes = t("dvp.mistakes", { returnObjects: true });
  const guidelines = t("dvp.guidelines", { returnObjects: true });
  const faqs = t("dvp.faqs", { returnObjects: true });

  return (
    <div className="min-h-screen bg-background" data-testid="dv-photo-requirements-page">
      <SEOHead
        title={t("dvp.heroTitle")}
        description={t("dvp.heroSubtitle")}
      />

      {/* HERO */}
      <section className="hero-gradient py-14 md:py-24" data-testid="dvp-hero">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <Badge variant="secondary" className="bg-teal-100 text-teal-800 hover:bg-teal-100 mb-5 font-medium">
            <Camera className="h-3.5 w-3.5 mr-1.5" />
            {t("dvp.badge")}
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-slate-900 mb-5">
            {t("dvp.heroTitle")}
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-slate-600 mb-8 max-w-2xl mx-auto">
            {t("dvp.heroSubtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={lp("/green-card-lottery#eligibility")} data-testid="dvp-hero-elig">
              <Button size="lg" className="bg-teal-700 hover:bg-teal-800 text-white font-medium shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 w-full sm:w-auto px-8 py-6 text-base">
                <ShieldCheck className="mr-2 h-4 w-4" /> {t("dvp.ctaEligibility")}
              </Button>
            </Link>
            <Link to={lp("/how-to-fill-dv-lottery-form")} data-testid="dvp-hero-form">
              <Button size="lg" variant="outline" className="border-slate-200 hover:bg-slate-50 font-medium w-full sm:w-auto px-8 py-6 text-base">
                <ClipboardList className="mr-2 h-4 w-4" /> {t("dvp.ctaFormGuide")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 1 — OFFICIAL PHOTO RULES */}
      <section className="py-14 md:py-20 bg-white" data-testid="dvp-rules">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvp.rulesLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvp.rulesTitle")}</h2>
            <p className="mt-3 text-base text-slate-600 max-w-2xl mx-auto">{t("dvp.rulesIntro")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Array.isArray(rules) && rules.map((rule, i) => {
              const Icon = ruleIcons[i] || Camera;
              return (
                <Card key={i} className="border-slate-100 shadow-sm hover:shadow-md transition-all duration-300" data-testid={`dvp-rule-${i}`}>
                  <CardContent className="p-5">
                    <div className={`h-10 w-10 rounded-xl ${ruleColors[i] || ruleColors[0]} flex items-center justify-center mb-3`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900 mb-1.5">{rule.title}</h3>
                    <p className="text-sm leading-relaxed text-slate-600">{rule.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 2 — PHOTO SIZE AND FORMAT */}
      <section className="py-14 md:py-20 bg-slate-50/60" data-testid="dvp-specs">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvp.specsLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvp.specsTitle")}</h2>
            <p className="mt-3 text-base text-slate-600 max-w-2xl mx-auto">{t("dvp.specsIntro")}</p>
          </div>
          <div className="space-y-4">
            {Array.isArray(specs) && specs.map((spec, i) => {
              const Icon = specIcons[i] || FileImage;
              return (
                <Card key={i} className="border-slate-100 shadow-sm" data-testid={`dvp-spec-${i}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className={`h-10 w-10 rounded-full ${specColors[i] || specColors[0]} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-slate-900 mb-1">{spec.title}</h3>
                        <p className="text-sm leading-relaxed text-slate-600">{spec.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 3 — COMMON PHOTO MISTAKES */}
      <section className="py-14 md:py-20 bg-white" data-testid="dvp-mistakes">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvp.mistakesLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvp.mistakesTitle")}</h2>
            <p className="mt-3 text-base text-slate-600">{t("dvp.mistakesIntro")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Array.isArray(mistakes) && mistakes.map((m, i) => (
              <Card key={i} className={`border shadow-sm ${mistakeColors[i] || mistakeColors[0]}`} data-testid={`dvp-mistake-${i}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900 mb-1">{m.title}</h3>
                      <p className="text-sm text-slate-600">{m.desc}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to={lp("/auth?mode=register&redirect=%2Fchecklists")} data-testid="dvp-mistakes-cta">
              <Button className="bg-teal-700 hover:bg-teal-800 text-white font-medium">
                <ClipboardList className="mr-2 h-4 w-4" /> {t("dvp.ctaChecklist")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 4 — EXAMPLE PHOTO GUIDELINES */}
      <section className="py-14 md:py-20 bg-slate-50/60" data-testid="dvp-guidelines">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvp.guidelinesLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvp.guidelinesTitle")}</h2>
            <p className="mt-3 text-base text-slate-600 max-w-2xl mx-auto">{t("dvp.guidelinesIntro")}</p>
          </div>
          <div className="space-y-4">
            {Array.isArray(guidelines) && guidelines.map((g, i) => (
              <Card key={i} className="border-slate-100 shadow-sm" data-testid={`dvp-guideline-${i}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="h-8 w-8 rounded-full bg-teal-700 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {i + 1}
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-slate-900 mb-1">{g.title}</h3>
                      <p className="text-sm leading-relaxed text-slate-600">{g.desc}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <DVGuidesNav currentPath="/dv-lottery-photo-requirements" />

      {/* SECTION 5 — FAQ */}
      <section className="py-14 md:py-20 bg-white" data-testid="dvp-faq">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvp.faqLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvp.faqTitle")}</h2>
          </div>
          <div className="space-y-3">
            {Array.isArray(faqs) && faqs.map((faq, i) => {
              const isOpen = openFaq === i;
              return (
                <Card key={i} className="border-slate-100 shadow-sm overflow-hidden" data-testid={`dvp-faq-${i}`}>
                  <button
                    className="w-full flex items-start gap-3 p-5 text-left hover:bg-slate-50/50 transition-colors"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    data-testid={`dvp-faq-toggle-${i}`}
                  >
                    <HelpCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm font-semibold text-slate-900 flex-1">{faq.q}</span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5">
                      <p className="text-sm leading-relaxed text-slate-600 ml-8">{faq.a}</p>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-14 md:py-24 bg-teal-700" data-testid="dvp-final-cta">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <Compass className="h-10 w-10 text-teal-200 mx-auto mb-5" />
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-white mb-3">{t("dvp.finalTitle")}</h2>
          <p className="text-base text-teal-100 mb-8 max-w-xl mx-auto">{t("dvp.finalSubtitle")}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={lp("/green-card-lottery#eligibility")} data-testid="dvp-final-elig">
              <Button size="lg" className="bg-white text-teal-700 hover:bg-teal-50 font-medium shadow-lg w-full sm:w-auto px-6 py-6 text-base">
                <ShieldCheck className="mr-2 h-4 w-4" /> {t("dvp.ctaEligibility")}
              </Button>
            </Link>
            <Link to={lp("/auth?mode=register")} data-testid="dvp-final-start">
              <Button size="lg" variant="outline" className="border-teal-400 text-white hover:bg-teal-600 font-medium w-full sm:w-auto px-6 py-6 text-base">
                {t("dvp.ctaStartPrep")} <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
